/**
 * DiffViewer component for visualizing differences between two text contents
 */
import { useMemo, useState } from 'react';
import { Plus, Minus, RotateCw, Eye, EyeOff, Copy, ArrowUpDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface DiffChange {
  type: 'add' | 'remove' | 'modify' | 'unchanged';
  oldLineNumber?: number;
  newLineNumber?: number;
  oldContent?: string;
  newContent?: string;
  content: string;
}

interface DiffViewerProps {
  oldContent: string;
  newContent: string;
  oldTitle?: string;
  newTitle?: string;
  className?: string;
}

export function DiffViewer({
  oldContent,
  newContent,
  oldTitle = 'Original',
  newTitle = 'Modified',
  className,
}: DiffViewerProps) {
  const [viewMode, setViewMode] = useState<'split' | 'unified'>('split');
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [showUnchanged, setShowUnchanged] = useState(true);

  // Calculate diff changes
  const diffChanges = useMemo(() => {
    return calculateDiff(oldContent, newContent);
  }, [oldContent, newContent]);

  // Get statistics
  const stats = useMemo(() => {
    const added = diffChanges.filter((change) => change.type === 'add').length;
    const removed = diffChanges.filter((change) => change.type === 'remove').length;
    const modified = diffChanges.filter((change) => change.type === 'modify').length;

    return { added, removed, modified };
  }, [diffChanges]);

  // Filter changes based on showUnchanged setting
  const filteredChanges = useMemo(() => {
    if (showUnchanged) return diffChanges;
    return diffChanges.filter((change) => change.type !== 'unchanged');
  }, [diffChanges, showUnchanged]);

  const handleCopyDiff = async () => {
    try {
      const diffText = generateDiffText(diffChanges, oldTitle, newTitle);
      await navigator.clipboard.writeText(diffText);
    } catch (error) {}
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-muted/20">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold">Diff View</h3>

          {/* Statistics */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-green-700 border-green-200">
              <Plus className="h-3 w-3 mr-1" />+{stats.added}
            </Badge>
            <Badge variant="outline" className="text-red-700 border-red-200">
              <Minus className="h-3 w-3 mr-1" />-{stats.removed}
            </Badge>
            <Badge variant="outline" className="text-blue-700 border-blue-200">
              <RotateCw className="h-3 w-3 mr-1" />~{stats.modified}
            </Badge>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setViewMode(viewMode === 'split' ? 'unified' : 'split')}
                >
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  {viewMode === 'split' ? 'Unified' : 'Split'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Switch to {viewMode === 'split' ? 'unified' : 'split'} view
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Show Line Numbers Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={showLineNumbers ? 'default' : 'outline'}
                  onClick={() => setShowLineNumbers(!showLineNumbers)}
                >
                  <FileText className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle line numbers</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Show Unchanged Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={showUnchanged ? 'default' : 'outline'}
                  onClick={() => setShowUnchanged(!showUnchanged)}
                >
                  {showUnchanged ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{showUnchanged ? 'Hide' : 'Show'} unchanged lines</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Copy Diff */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" onClick={handleCopyDiff}>
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy diff to clipboard</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* File Headers */}
      {viewMode === 'split' && (
        <div className="flex border-b">
          <div className="flex-1 px-4 py-2 bg-red-50 border-r">
            <div className="flex items-center gap-2">
              <Minus className="h-4 w-4 text-red-600" />
              <span className="font-medium text-red-900">{oldTitle}</span>
            </div>
          </div>
          <div className="flex-1 px-4 py-2 bg-green-50">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-900">{newTitle}</span>
            </div>
          </div>
        </div>
      )}

      {/* Diff Content */}
      <ScrollArea className="flex-1">
        <div className={cn('font-mono text-sm', viewMode === 'split' ? 'flex' : '')}>
          {viewMode === 'split' ? (
            <SplitView
              changes={filteredChanges}
              showLineNumbers={showLineNumbers}
              oldTitle={oldTitle}
              newTitle={newTitle}
            />
          ) : (
            <UnifiedView changes={filteredChanges} showLineNumbers={showLineNumbers} />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Split view component
function SplitView({
  changes,
  showLineNumbers,
  oldTitle: _oldTitle,
  newTitle: _newTitle,
}: {
  changes: DiffChange[];
  showLineNumbers: boolean;
  oldTitle: string;
  newTitle: string;
}) {
  return (
    <>
      {/* Old Content */}
      <div className="flex-1 border-r">
        {changes.map((change, index) => {
          if (change.type === 'add') {
            return (
              <div key={index} className="flex bg-gray-50">
                {showLineNumbers && (
                  <div className="w-12 px-2 py-1 text-muted-foreground text-right text-xs"></div>
                )}
                <div className="flex-1 px-2 py-1 text-muted-foreground">&nbsp;</div>
              </div>
            );
          }

          const bgColor =
            change.type === 'remove' ? 'bg-red-50' : change.type === 'modify' ? 'bg-yellow-50' : '';

          const textColor =
            change.type === 'remove'
              ? 'text-red-900'
              : change.type === 'modify'
                ? 'text-yellow-900'
                : '';

          return (
            <div key={index} className={cn('flex', bgColor)}>
              {showLineNumbers && (
                <div className={cn('w-12 px-2 py-1 text-right text-xs', textColor)}>
                  {change.oldLineNumber}
                </div>
              )}
              <div className={cn('flex-1 px-2 py-1', textColor)}>
                {change.oldContent || change.content}
              </div>
            </div>
          );
        })}
      </div>

      {/* New Content */}
      <div className="flex-1">
        {changes.map((change, index) => {
          if (change.type === 'remove') {
            return (
              <div key={index} className="flex bg-gray-50">
                {showLineNumbers && (
                  <div className="w-12 px-2 py-1 text-muted-foreground text-right text-xs"></div>
                )}
                <div className="flex-1 px-2 py-1 text-muted-foreground">&nbsp;</div>
              </div>
            );
          }

          const bgColor =
            change.type === 'add' ? 'bg-green-50' : change.type === 'modify' ? 'bg-yellow-50' : '';

          const textColor =
            change.type === 'add'
              ? 'text-green-900'
              : change.type === 'modify'
                ? 'text-yellow-900'
                : '';

          return (
            <div key={index} className={cn('flex', bgColor)}>
              {showLineNumbers && (
                <div className={cn('w-12 px-2 py-1 text-right text-xs', textColor)}>
                  {change.newLineNumber}
                </div>
              )}
              <div className={cn('flex-1 px-2 py-1', textColor)}>
                {change.newContent || change.content}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Unified view component
function UnifiedView({
  changes,
  showLineNumbers,
}: {
  changes: DiffChange[];
  showLineNumbers: boolean;
}) {
  return (
    <div className="w-full">
      {changes.map((change, index) => {
        const bgColor =
          change.type === 'add'
            ? 'bg-green-50'
            : change.type === 'remove'
              ? 'bg-red-50'
              : change.type === 'modify'
                ? 'bg-yellow-50'
                : '';

        const textColor =
          change.type === 'add'
            ? 'text-green-900'
            : change.type === 'remove'
              ? 'text-red-900'
              : change.type === 'modify'
                ? 'text-yellow-900'
                : '';

        const prefix =
          change.type === 'add'
            ? '+'
            : change.type === 'remove'
              ? '-'
              : change.type === 'modify'
                ? '~'
                : ' ';

        return (
          <div key={index} className={cn('flex', bgColor)}>
            <div className={cn('w-4 px-1 py-1 text-center text-xs', textColor)}>{prefix}</div>
            {showLineNumbers && (
              <div className={cn('w-12 px-2 py-1 text-right text-xs', textColor)}>
                {change.oldLineNumber || change.newLineNumber}
              </div>
            )}
            <div className={cn('flex-1 px-2 py-1', textColor)}>{change.content}</div>
          </div>
        );
      })}
    </div>
  );
}

// Simple diff calculation function
function calculateDiff(oldContent: string, newContent: string): DiffChange[] {
  const oldLines = oldContent.split('\n');
  const newLines = newContent.split('\n');
  const changes: DiffChange[] = [];

  // Simple line-by-line comparison
  const maxLength = Math.max(oldLines.length, newLines.length);

  for (let i = 0; i < maxLength; i++) {
    const oldLine = oldLines[i];
    const newLine = newLines[i];

    if (oldLine === undefined) {
      // Line added
      changes.push({
        type: 'add',
        newLineNumber: i + 1,
        newContent: newLine,
        content: newLine,
      });
    } else if (newLine === undefined) {
      // Line removed
      changes.push({
        type: 'remove',
        oldLineNumber: i + 1,
        oldContent: oldLine,
        content: oldLine,
      });
    } else if (oldLine !== newLine) {
      // Line modified - show both old and new
      changes.push({
        type: 'remove',
        oldLineNumber: i + 1,
        oldContent: oldLine,
        content: oldLine,
      });
      changes.push({
        type: 'add',
        newLineNumber: i + 1,
        newContent: newLine,
        content: newLine,
      });
    } else {
      // Line unchanged
      changes.push({
        type: 'unchanged',
        oldLineNumber: i + 1,
        newLineNumber: i + 1,
        content: oldLine,
      });
    }
  }

  return changes;
}

// Generate unified diff text format for clipboard
function generateDiffText(changes: DiffChange[], oldTitle: string, newTitle: string): string {
  const lines = [`--- ${oldTitle}`, `+++ ${newTitle}`, ''];

  changes.forEach((change) => {
    const prefix = change.type === 'add' ? '+' : change.type === 'remove' ? '-' : ' ';
    lines.push(`${prefix}${change.content}`);
  });

  return lines.join('\n');
}
